/**
 * Store actions for merge operations.
 *
 * Integrates mergeMaterialsCommand into the Zustand store state management.
 */

import type { WorkflowGraph } from '@/graph';
import type { Store } from '../types';
import { mergeMaterials, MergeMaterialsError } from '@/commands/mergeMaterialsCommand';
import { pushUndo } from './historyActions';
import { withSyncGuard } from '../sync/syncFlag';
import { hydrateFromSnapshot } from '@/graph/domain/snapshot';
import { recomputeDerivation } from '../recomputeDerivation';

type SetState = (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void;
type GetState = () => Store;

export function createMergeActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  return {
    /**
     * Merge two materials from different connected branches.
     *
     * Combines sourceNode and targetNode into a single mergedNode:
     * - Rewires all edges from sourceNode → targetNode
     * - Deletes sourceNode
     * - Sums quantities
     * - Renumbers creationOrder globally
     *
     * Direction rules:
     * - ROOT clicked: sourceNodeId = clicked, targetNodeId = candidate
     * - LEAF clicked: sourceNodeId = candidate, targetNodeId = clicked
     *
     * Throws MergeMaterialsError if validation fails.
     */
    mergeMaterials(
      sourceNodeId: string,
      targetNodeId: string,
      contextType: 'ROOT' | 'LEAF',
    ): void {
      const state = get();

      try {
        // Save state for undo
        pushUndo(get, set, `Merge materials: ${sourceNodeId} into ${targetNodeId}`);

        // Create a complete mutable copy of graphData for the command to mutate
        const graphDataCopy = {
          ...state.graphData,
          materialNodes: { ...state.graphData.materialNodes },
          operationNodes: { ...state.graphData.operationNodes },
          flowEdges: { ...state.graphData.flowEdges },
          groups: state.graphData.groups ? { ...state.graphData.groups } : {},
        };

        // Deep copy all materials and operations to ensure mutations are tracked
        for (const matId of Object.keys(graphDataCopy.materialNodes)) {
          graphDataCopy.materialNodes[matId] = { ...graphDataCopy.materialNodes[matId] };
        }

        for (const opId of Object.keys(graphDataCopy.operationNodes)) {
          const op = graphDataCopy.operationNodes[opId];
          graphDataCopy.operationNodes[opId] = { ...op };

          // Deep copy transferRow if present
          if (op.transferRow) {
            graphDataCopy.operationNodes[opId].transferRow = { ...op.transferRow };
          }
        }

        const result = mergeMaterials(graphDataCopy, { sourceNodeId, targetNodeId }, contextType);

        // Use the actual deleted node ID from the result (could be either source or target)
        const deletedNodeId = result.deletedSourceNodeId;
        const mergedNodeId = deletedNodeId === sourceNodeId ? targetNodeId : sourceNodeId;

        // Update store ATOMICALLY: merge result + recompute derivation all in one set call
        // This ensures the store is consistent before any graph updates happen
        set((s) => {
          // Start with graphDataCopy
          let updatedGraphData = graphDataCopy;

          // Recompute derivation to refresh translations and compositions downstream
          updatedGraphData = recomputeDerivation(updatedGraphData);
          
          // Calculate which cellIds to keep (remove deleted node from selection)
          const newCellIds = s.selection.cellIds.filter((id) => id !== deletedNodeId);
          
          return {
            graphData: updatedGraphData,
            selection: { cellIds: newCellIds },
            ui: { ...s.ui, isDirty: true },
          };
        });

        // Now rebuild graph visualization with the updated store state
        const graph = getGraph();
        if (graph) {
          const state3 = get();
          withSyncGuard(set, () => {
            // Completely rebuild the graph from the new graphData
            // Convert store's record-based structure to array-based GraphSnapshot
            const graphSnapshot = {
              materialNodes: Object.values(state3.graphData.materialNodes),
              operationNodes: Object.values(state3.graphData.operationNodes),
              flowEdges: Object.values(state3.graphData.flowEdges),
              groups: Object.values(state3.graphData.groups),
            };
            hydrateFromSnapshot(graph, graphSnapshot);
          });
        }
      } catch (err) {
        if (err instanceof MergeMaterialsError) {
          const errorMessages: Record<string, string> = {
            SAME_MATERIAL: 'Cannot merge a material with itself',
            SAME_COMPONENT: 'Materials must be in different branches',
            INVALID_ENDPOINTS: 'Both materials must be endpoints (start/end)',
            SOURCE_NOT_FOUND: 'Source material not found',
            TARGET_NOT_FOUND: 'Target material not found',
            BOTH_GROUPED: 'Cannot merge materials that are in groups',
            GROUP_MEMBER_MERGE: 'Cannot merge group members',
          };

          const message = errorMessages[err.code] || err.message;
          throw new Error(`Merge validation failed: ${message}`);
        }
        throw err;
      }
    },
  };
}
